/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { forwardRef, Ref, useEffect, useState } from 'react';
import { FormControl } from '../../../FormControl/FormControl';
import { FormControlOption } from '../../../FormControl/FormControl.defs';
import { FormControlProps } from '../../../FormControl/FormControl.props';
import { FORM_CONTROL_TYPE_CHECKBOX } from '../../../FormControl/types/FormControlType.types';
import { FormField } from '../../../FormField';
import { CheckboxRadioProps, checkboxRadioPropsDefaults } from '../CheckboxRadio.props';
import { CHECKBOX_RADIO_TYPE_PROMINENT } from '../CheckboxRadio.types';

/**
 * SDS Checkbox List component.
 */
export const CheckboxList = forwardRef((props: CheckboxRadioProps, ref: Ref<HTMLInputElement>): JSX.Element => {
  const {
    checkboxListCallback,
    classes,
    disabled,
    fieldLabel,
    hideLabel,
    id,
    name,
    options,
    readOnly,
    variant,
    ...rest
  } = {
    ...checkboxRadioPropsDefaults,
    ...props,
  };
  const inputRef = ref as Ref<HTMLInputElement>;
  const listClass = `sds-form-control-list sds-form-control--${variant}`;

  // Remove extra content (JSX) from options data so we can stringify it later
  const initialValue = options.map(({ extraContent, ...item }) => item);
  const [currentOptions, setCurrentOptions] = useState<Array<FormControlOption>>(initialValue);

  const onCheckboxClick = (option: FormControlOption): void => {
    const optionsToChange = structuredClone(currentOptions);
    const updatedOptions = optionsToChange.map((item) =>
      item.key === option.key ? { ...item, checked: option.checked } : item,
    );

    setCurrentOptions(updatedOptions);
    checkboxListCallback && checkboxListCallback(updatedOptions);
  };

  const getOptions = (): Array<JSX.Element> => {
    return currentOptions.map((option, index): JSX.Element => {
      const optionClass = clsx(`sds-form-control-option`, {
        [`disabled`]: disabled || option.disabled,
        [`readOnly`]: readOnly,
      });

      // Get any elements from original option data
      const providedOption = options[index];
      const optionContent = providedOption ? providedOption.extraContent : <></>;
      const formControlOption = {
        ...option,
        extraContent: optionContent,
      };

      const checkboxProps = { ...props } as FormControlProps;
      checkboxProps.checkboxClick = onCheckboxClick;
      checkboxProps.checked = option.checked;
      checkboxProps.disabled = disabled || option.disabled;
      checkboxProps.option = formControlOption;
      checkboxProps.type = FORM_CONTROL_TYPE_CHECKBOX;

      return (
        <li className="sds-form-control-item" data-key={option.key} key={option.key}>
          <FormControl {...checkboxProps} classes={optionClass} ref={inputRef} />
        </li>
      );
    });
  };

  useEffect((): void => {
    const providedOptions = options.map(({ extraContent, ...item }) => item);
    let shouldSetCurrentOptions = false;

    // Refresh the list based on the number of options changing.
    // If the lengths are the same, check the option content.
    if (currentOptions.length !== options.length) {
      shouldSetCurrentOptions = true;
    } else {
      options.forEach((option, index) => {
        if (option.label !== currentOptions[index].label) {
          shouldSetCurrentOptions = true;
        }
      });
    }

    if (shouldSetCurrentOptions) {
      setCurrentOptions(providedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // Configure field
  const formFieldProps = { ...props };
  formFieldProps.classes = clsx(`sds-form-controls`, classes);

  return (
    <FormField {...formFieldProps}>
      <div className="sds-checkbox-group" {...rest}>
        <input id={id} name={name} ref={ref} type="hidden" value={JSON.stringify(currentOptions)} />
        {variant === CHECKBOX_RADIO_TYPE_PROMINENT ? (
          <div className="sds-field-container">
            <ul className={listClass}>{getOptions()}</ul>
          </div>
        ) : (
          <ul className={listClass}>{getOptions()}</ul>
        )}
      </div>
    </FormField>
  );
});
